import { Component, computed, input, output } from '@angular/core';
import { RidKeyGroup } from './rid-key-group/rid-key-group';
import { RidKeyboardKey } from './rid-keyboard-key/rid-keyboard-key';
import { RidKeyboardRow } from './rid-keyboard-row/rid-keyboard-row';
import { RidLongVowelKeyWrapper } from './rid-long-vowel-key-wrapper/rid-long-vowel-key-wrapper';
import { allKeys, keyboardGroups, RidSyllabicKeyboardUtils, TKey, TKeyboardLayout, TKeyValue } from './rid-syllabic-keyboard.utils';

@Component({
  selector: 'rid-syllabic-keyboard',
  imports: [RidKeyboardRow, RidKeyGroup, RidLongVowelKeyWrapper, RidKeyboardKey],
  templateUrl: './rid-syllabic-keyboard.html',
})
export class RidSyllabicKeyboard {
  layout = input<TKeyboardLayout | undefined>({
    KLINKERS: 'ENABLED',
    LANGE_KLINKERS: 'ENABLED',
    KORTE_LANGE_KLINKERS: 'ENABLED',
    SPACE: 'ENABLED',
    DUBBELE_KLINKERS: 'ENABLED',
    DRIEDUBBELE_KLINKERS: 'ENABLED',
    VIERDUBBELE_KLINKERS: 'ENABLED',
    MEDEKLINKERS: 'ENABLED',
    EXOTEN: 'ENABLED',
    SJWAS: 'ENABLED',
    KLANKVOET: 'ENABLED',
    RULE: 'ENABLED',
  });
  mode = input<'GROUP' | 'KLANK' | 'BOTH'>('BOTH');
  mask = input<TKeyValue[] | undefined>(undefined);
  highlightMask = input<TKeyValue[] | undefined>(undefined);
  disabled = input<boolean>(false);

  modeMask = computed<TKeyValue[]>(() => {
    switch (this.mode()) {
      case 'GROUP':
        return Object.values(keyboardGroups)
          .flat()
          .filter((k) => k.type === 'GROUP')
          .map((k) => k.value);
      case 'KLANK':
        return Object.values(keyboardGroups)
          .flat()
          .filter((k) => k.type !== 'GROUP')
          .map((k) => k.value);
      default:
        return [...allKeys].map((k) => k.value);
    }
  });
  finalMask = computed(() => RidSyllabicKeyboardUtils.combineMasks(this.modeMask(), this.mask()));

  typed = output<TKey>();

  convertValueToKey = RidSyllabicKeyboardUtils.convertValueToKey.bind(RidSyllabicKeyboardUtils);
  keyboardKeyGroups = keyboardGroups;
  doubleVowelKeys = [
    [
      { type: 'KLANK', value: 'aa' },
      { type: 'KLANK', value: '(a)a' },
    ],
    [
      { type: 'KLANK', value: 'ee' },
      { type: 'KLANK', value: '(e)e' },
    ],
    [
      { type: 'KLANK', value: 'oo' },
      { type: 'KLANK', value: '(o)o' },
    ],
    [
      { type: 'KLANK', value: 'uu' },
      { type: 'KLANK', value: '(u)u' },
    ],
  ] as [longKey: TKey, shortKey: TKey][];

  isEnabled(value: TKeyValue): boolean {
    if (this.disabled()) return false;
    if (this.isHidden(value)) return false;

    const key = RidSyllabicKeyboardUtils.convertValueToKey(value);

    const groupName = RidSyllabicKeyboardUtils.getKeyGroupName(key);
    const layout = this.layout();
    if (layout && layout[groupName] === 'DISABLED') {
      return false;
    }

    const mask = this.finalMask();
    const isInMask = mask ? mask.includes(key.value) : true;
    return isInMask;
  }

  hasHighlight(value: TKeyValue): boolean {
    const highlightMask = this.highlightMask();
    return !!highlightMask && highlightMask.includes(value);
  }

  isHidden(value: TKeyValue) {
    const key = RidSyllabicKeyboardUtils.convertValueToKey(value);
    const groupName = RidSyllabicKeyboardUtils.getKeyGroupName(key);
    const layout = this.layout();
    if (!layout) return false;

    return layout[groupName] === 'HIDDEN';
  }

  type(value: TKeyValue) {
    const key = RidSyllabicKeyboardUtils.convertValueToKey(value);
    this.typed.emit(key);
  }
}
